import { c as _c } from "react/compiler-runtime";
import { useControlledSwr } from '@systemeio/util';
import { AutomationRuleActionsDataEnum } from 'modules/automation-rules/actions/enums/action-enum';
import { getShouldFetchActions } from 'modules/automation-rules/actions/utils/get-should-fetch-actions';
import { AUTOMATION_RULES_FILTER_API } from 'modules/automation-rules/api/automation-rules-api';
import { FilterBaseApiInterface, FilterEmailApiInterface } from 'modules/automation-rules/types/automation-rule-filter-interface';
import { AutomationRuleBaseDataInterface } from 'modules/automation-rules/types/automation-rule-interface';
import { geDataFromApiData, getData, getPipelineData } from 'modules/automation-rules/utils/get-filter-data';
import { useCampaignsList } from 'modules/campaigns/hooks/use-campaigns-list';
import { useCourseBundlesListAll } from 'modules/courses/bundles/hooks/use-course-bundlies-list-all';
import { coursesDependencies } from 'modules/courses/constants/courses-dependencies';
import { usePipelineList } from 'modules/crm/pipeline/hooks/use-pipeline-list';
import { useSmsTemplatesListAll } from 'modules/sms/hooks/use-sms-templates-list-all';
import { SmsTemplateListAllInterface } from 'modules/sms/types';
import { useTagsAll } from 'modules/tags/hooks/use-tags-all';
import { TagListAllInterface } from 'modules/tags/types/tag-interface';
import { Dispatch, SetStateAction, useState } from 'react';
import { useFetch } from 'shared/hooks/use-fetch';
import { RequestMethodsEnum } from 'shared/hooks/use-fetch/types';
import useUser from 'shared/hooks/use-user';
import useSWR, { KeyedMutator } from 'swr';
export interface ActionsDataInterface {
  campaignData?: AutomationRuleBaseDataInterface[];
  tagData?: AutomationRuleBaseDataInterface[];
  communitiesData?: AutomationRuleBaseDataInterface[];
  emailData?: AutomationRuleBaseDataInterface[];
  specificEmailData?: AutomationRuleBaseDataInterface[];
  courseData?: AutomationRuleBaseDataInterface[];
  courseBundleData?: AutomationRuleBaseDataInterface[];
  pipelineData?: AutomationRuleBaseDataInterface[];
  smsTemplateData?: AutomationRuleBaseDataInterface[];
  mutateEmailData?: KeyedMutator<FilterEmailApiInterface[]>;
  mutateSpecificEmailData?: KeyedMutator<FilterEmailApiInterface[]>;
  mutateTagsData?: KeyedMutator<TagListAllInterface>;
  mutateSmsTemplateData?: KeyedMutator<SmsTemplateListAllInterface>;
}
export function useActionsData() {
  const $ = _c(47);
  const {
    user
  } = useUser();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getShouldFetchActions();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [shouldFetch, setShouldFetch] = useState(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      method: RequestMethodsEnum.get
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    fetcher: baseFetcher
  } = useFetch(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      method: RequestMethodsEnum.get
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    fetcher: emailFetcher
  } = useFetch(t2);
  const {
    tags: tagsData,
    mutate: mutateTagsData
  } = useTagsAll(shouldFetch.tag);
  let t3;
  if ($[3] !== shouldFetch.mailing || $[4] !== user) {
    t3 = () => {
      if (user && shouldFetch.mailing) {
        return `${AUTOMATION_RULES_FILTER_API}/action-type/email`;
      }
      return null;
    };
    $[3] = shouldFetch.mailing;
    $[4] = user;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    data: emailsData,
    mutate: mutateEmailData
  } = useSWR(t3, emailFetcher);
  let t4;
  if ($[6] !== shouldFetch.specific_mailing || $[7] !== user) {
    t4 = () => {
      if (user && shouldFetch.specific_mailing) {
        return `${AUTOMATION_RULES_FILTER_API}/action-type/specific-email`;
      }
      return null;
    };
    $[6] = shouldFetch.specific_mailing;
    $[7] = user;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const {
    data: specificEmailsData,
    mutate: mutateSpecificEmailData
  } = useSWR(t4, emailFetcher);
  let t5;
  if ($[9] !== shouldFetch.course || $[10] !== user) {
    t5 = () => user && shouldFetch.course && [`${AUTOMATION_RULES_FILTER_API}/action-type/course`, ...coursesDependencies];
    $[9] = shouldFetch.course;
    $[10] = user;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const {
    data: coursesData
  } = useControlledSwr(t5, baseFetcher);
  let t6;
  if ($[12] !== shouldFetch.community || $[13] !== user) {
    t6 = () => {
      if (user && shouldFetch.community) {
        return `${AUTOMATION_RULES_FILTER_API}/action-type/community`;
      }
    };
    $[12] = shouldFetch.community;
    $[13] = user;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const {
    data: communitiesData
  } = useSWR(t6, baseFetcher);
  const {
    courseBundlesListAll
  } = useCourseBundlesListAll(shouldFetch?.course_bundle);
  const {
    campaignsList
  } = useCampaignsList(shouldFetch.campaign);
  const {
    pipelineStage
  } = usePipelineList(shouldFetch?.pipeline);
  const {
    data: smsTemplates,
    mutate: mutateSmsTemplateData
  } = useSmsTemplatesListAll(shouldFetch?.sms_template);
  let t7;
  if ($[15] !== campaignsList) {
    t7 = geDataFromApiData(campaignsList);
    $[15] = campaignsList;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const t8 = tagsData?.items;
  let t9;
  if ($[17] !== t8) {
    t9 = getData(t8);
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== communitiesData) {
    t10 = getData(communitiesData);
    $[19] = communitiesData;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== emailsData) {
    t11 = emailsData?.map(_temp);
    $[21] = emailsData;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== specificEmailsData) {
    t12 = specificEmailsData?.map(_temp2);
    $[23] = specificEmailsData;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== coursesData) {
    t13 = getData(coursesData);
    $[25] = coursesData;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== courseBundlesListAll) {
    t14 = geDataFromApiData(courseBundlesListAll);
    $[27] = courseBundlesListAll;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== pipelineStage) {
    t15 = getPipelineData(pipelineStage);
    $[29] = pipelineStage;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== smsTemplates) {
    t16 = getData(smsTemplates);
    $[31] = smsTemplates;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== mutateEmailData || $[34] !== mutateSmsTemplateData || $[35] !== mutateSpecificEmailData || $[36] !== mutateTagsData || $[37] !== t10 || $[38] !== t11 || $[39] !== t12 || $[40] !== t13 || $[41] !== t14 || $[42] !== t15 || $[43] !== t16 || $[44] !== t7 || $[45] !== t9) {
    t17 = {
      data: {
        campaignData: t7,
        tagData: t9,
        communitiesData: t10,
        emailData: t11,
        specificEmailData: t12,
        courseData: t13,
        courseBundleData: t14,
        pipelineData: t15,
        smsTemplateData: t16,
        mutateEmailData,
        mutateSpecificEmailData,
        mutateTagsData,
        mutateSmsTemplateData
      },
      setShouldFetch
    };
    $[33] = mutateEmailData;
    $[34] = mutateSmsTemplateData;
    $[35] = mutateSpecificEmailData;
    $[36] = mutateTagsData;
    $[37] = t10;
    $[38] = t11;
    $[39] = t12;
    $[40] = t13;
    $[41] = t14;
    $[42] = t15;
    $[43] = t16;
    $[44] = t7;
    $[45] = t9;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  return t17;
}
function _temp2(el_0) {
  return {
    id: el_0.id,
    caption: el_0.subject
  };
}
function _temp(el) {
  return {
    id: el.id,
    caption: el.subject
  };
}